import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useQuery } from "react-query";
import useAuthentication from "../../hooks/useAuthentication";
import { fetchAiClinicWalletService } from "../../services/aiWalletService";
import type { AiClinicWalletItem } from "../../Types/AiWallet/AiWalletTypes";
import { AI_WALLET_REFRESH_EVENT } from "./aiWalletEvents";

type AiWalletContextValue = {
  wallet: AiClinicWalletItem | null;
  balanceCredits: number | null;
  isLoading: boolean;
  refreshWallet: () => void;
};

const AiWalletContext = createContext<AiWalletContextValue | null>(null);

function pickWalletEntry(items: AiClinicWalletItem[] | null | undefined) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return items[0];
}

export function AiWalletProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuthentication();

  const { data, isLoading, refetch } = useQuery(
    ["aiClinicWallet"],
    fetchAiClinicWalletService,
    {
      enabled: Boolean(isAuthenticated),
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  );

  const refreshWallet = useCallback(() => {
    if (!isAuthenticated) return;
    void refetch();
  }, [isAuthenticated, refetch]);

  useEffect(() => {
    const onRefresh = () => {
      refreshWallet();
    };

    window.addEventListener(AI_WALLET_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(AI_WALLET_REFRESH_EVENT, onRefresh);
  }, [refreshWallet]);

  const wallet = useMemo(() => {
    if (data?.responseIndicator !== "success") return null;
    return pickWalletEntry(data.response);
  }, [data]);

  const balanceCredits = wallet?.balanceCredits ?? null;

  const value = useMemo(
    (): AiWalletContextValue => ({
      wallet,
      balanceCredits,
      isLoading,
      refreshWallet,
    }),
    [balanceCredits, isLoading, refreshWallet, wallet],
  );

  return (
    <AiWalletContext.Provider value={value}>{children}</AiWalletContext.Provider>
  );
}

export function useAiWallet(): AiWalletContextValue {
  const ctx = useContext(AiWalletContext);
  if (!ctx) {
    throw new Error("useAiWallet must be used within AiWalletProvider");
  }
  return ctx;
}
